// Page Questionnaire Q1 - HALTES — Mode Focus
// 1 question = 1 écran, barre unifiée en haut, auto-advance
// Plus de panneau latéral, plus de boutons prev/next
// Flux wizard (1 question a la fois) + sequentiel metiers

import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { cn } from '../lib/utils'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Card, CardContent } from '../components/ui'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  QuestionWizard,
  QuestionnaireIntro,
  type ReponseValeurV4,
} from '../components/diagnostic'
import { api } from '../services/api'

interface QuestionData {
  id: string
  code: string
  libelle: string
  aide?: string
  axe?: string
  axeHaltes?: string
  themeQuestion?: string
  ordre: number
}

interface Theme {
  id: string
  code: string
  nom: string
  description?: string
  questions: QuestionData[]
}

interface SelectedMetier {
  code: string
  nom: string
  ordre: number
}

interface LocationState {
  metiersSelectionnes?: SelectedMetier[]
  metierIds?: string[]
  currentMetierIndex?: number
  _navTimestamp?: number
}

const HALTES_THEMES = [
  { id: 'H', code: 'H', nom: 'Hygiene', description: 'Proprete, vestiaires, sanitaires' },
  { id: 'A', code: 'A', nom: 'Acces', description: 'Circulations, echafaudages, echelles' },
  { id: 'LT', code: 'LT', nom: 'Livraisons + Taches', description: 'Manutention, postures, outils' },
  { id: 'E', code: 'E', nom: 'Energies', description: 'Electrique, thermique, mecanique' },
  { id: 'S', code: 'S', nom: 'Secours', description: 'Premiers secours, evacuation' },
]

export default function QuestionnaireQ1Page() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const locationState = location.state as LocationState | undefined

  const [currentMetierIndex, setCurrentMetierIndex] = useState(locationState?.currentMetierIndex ?? 0)

  useEffect(() => {
    const state = location.state as LocationState
    if (state?.currentMetierIndex !== undefined && state?._navTimestamp) {
      setCurrentMetierIndex(state.currentMetierIndex)
      setCurrentThemeIndex(0)
      setCurrentQuestionIndex(0)
      setIsReviewing(true)
      setShowIntro(false)
    }
  }, [(location.state as LocationState)?._navTimestamp])

  const [questionsByMetier, setQuestionsByMetier] = useState<Map<string, QuestionData[]>>(new Map())
  const [reponses, setReponses] = useState<Map<string, { valeur?: number; commentaire?: string }>>(new Map())
  const reponsesRef = useRef(reponses)
  reponsesRef.current = reponses
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving] = useState(false)
  const [q2Completed, setQ2Completed] = useState(false)

  const [currentThemeIndex, setCurrentThemeIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isReviewing, setIsReviewing] = useState(false)
  const [showIntro, setShowIntro] = useState(true)
  const [isRenewal, setIsRenewal] = useState(false)
  const hasAutoPositioned = useRef(false)

  const currentMetierId = metierIds[currentMetierIndex] || metierIds[0]
  const currentMetier = metiersSelectionnes[currentMetierIndex] || metiersSelectionnes[0]
  const allQuestions = questionsByMetier.get(currentMetierId) || []

  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const steps = useDiagnosticSteps('questionnaire-q1', diagnosticId, {
    totalMetiers: metierIds.length,
    currentMetierIndex: currentMetierIndex,
    q2Completed,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
    currentMetierCode: currentMetier?.code,
  }, diagnosticProgress)

  const getReponseKey = useCallback((questionId: string, metierId: string) => {
    return `${questionId}:${metierId}`
  }, [])

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)

      try {
        const questionsMap = new Map<string, QuestionData[]>()

        for (const metierId of metierIds) {
          const questionsData: QuestionData[] = await api.getQuestionsByMetier(metierId, 'Q1')
          questionsMap.set(metierId, questionsData)
        }

        setQuestionsByMetier(questionsMap)

        if (diagnosticId) {
          const diagnostic = await api.getDiagnostic(diagnosticId)
          if (diagnostic?.parentId) setIsRenewal(true)
          const existingReponses: any[] = diagnostic?.reponses || []
          const reponsesMap = new Map<string, { valeur: number; commentaire?: string }>()

          const hasQ2Responses = existingReponses.some((r: any) => !r.metierId)
          setQ2Completed(hasQ2Responses)

          existingReponses.forEach((r: any) => {
            const metierKey = r.metier?.code || r.metierId
            const key = metierKey ? getReponseKey(r.questionId, metierKey) : r.questionId
            reponsesMap.set(key, {
              valeur: r.valeur,
              commentaire: r.commentaire,
            })
          })

          setReponses(reponsesMap)
          if (reponsesMap.size > 0) setShowIntro(false)
        }
      } catch (error) {
        console.error('Erreur chargement questions:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId, metierIds, getReponseKey])

  // Auto-positionnement : après chargement, se placer sur la première question non-répondue
  useEffect(() => {
    if (isLoading || isLoadingData) return
    if (hasAutoPositioned.current) return
    if (locationState?._navTimestamp) return
    if (reponses.size === 0) return

    hasAutoPositioned.current = true

    // Déterminer le bon métier si pas fourni explicitement
    let targetMetierIndex = locationState?.currentMetierIndex ?? 0

    if (locationState?.currentMetierIndex === undefined && metierIds.length > 1) {
      for (let mi = 0; mi < metierIds.length; mi++) {
        const mid = metierIds[mi]
        const questions = questionsByMetier.get(mid) || []
        const hasUnanswered = questions.some(q => {
          const key = `${q.id}:${mid}`
          return reponses.get(key)?.valeur === undefined
        })
        if (hasUnanswered) {
          targetMetierIndex = mi
          break
        }
      }
    }

    if (targetMetierIndex !== currentMetierIndex) {
      setCurrentMetierIndex(targetMetierIndex)
    }

    // Trouver la première question non-répondue dans ce métier
    const targetMetierId = metierIds[targetMetierIndex] || metierIds[0]
    const questions = questionsByMetier.get(targetMetierId) || []

    const themedQuestions = HALTES_THEMES.map((theme) => ({
      ...theme,
      questions: questions
        .filter((q) => (q.axe || q.axeHaltes) === theme.code)
        .sort((a, b) => a.ordre - b.ordre),
    }))

    for (let ti = 0; ti < themedQuestions.length; ti++) {
      const theme = themedQuestions[ti]
      for (let qi = 0; qi < theme.questions.length; qi++) {
        const q = theme.questions[qi]
        const key = `${q.id}:${targetMetierId}`
        if (reponses.get(key)?.valeur === undefined) {
          setCurrentThemeIndex(ti)
          setCurrentQuestionIndex(qi)
          setShowIntro(false)
          return
        }
      }
    }

    // Toutes répondues — si c'est un renouvellement, démarrer en mode review à la question 1
    if (isRenewal) {
      setCurrentThemeIndex(0)
      setCurrentQuestionIndex(0)
      setIsReviewing(true)
    }
    setShowIntro(false)
  }, [isLoading, isLoadingData, questionsByMetier, reponses, metierIds, currentMetierIndex, locationState, isRenewal])

  const themesList: Theme[] = useMemo(() => {
    return HALTES_THEMES.map((theme) => ({
      ...theme,
      questions: allQuestions
        .filter((q) => (q.axe || q.axeHaltes) === theme.code)
        .sort((a, b) => a.ordre - b.ordre),
    }))
  }, [allQuestions])

  const themesWithQuestions = useMemo(() => {
    return themesList.filter((t) => t.questions.length > 0)
  }, [themesList])

  const currentTheme = useMemo(() => {
    const theme = themesList[currentThemeIndex]
    if (theme && theme.questions.length === 0) {
      const firstWithQuestions = themesList.find((t) => t.questions.length > 0)
      return firstWithQuestions || theme
    }
    return theme
  }, [themesList, currentThemeIndex])

  const currentQuestions = currentTheme?.questions || []

  const currentReponses = useMemo(() => {
    const map = new Map<string, { valeur?: number; commentaire?: string }>()
    allQuestions.forEach((q) => {
      const key = getReponseKey(q.id, currentMetierId)
      const reponse = reponses.get(key)
      if (reponse) {
        map.set(q.id, reponse)
      }
    })
    return map
  }, [allQuestions, currentMetierId, reponses, getReponseKey])

  const themeProgress = useMemo(() => {
    return themesList.map((theme) => {
      const answered = theme.questions.filter((q) => currentReponses.get(q.id)?.valeur !== undefined).length
      return {
        code: theme.code,
        answered,
        total: theme.questions.length,
      }
    })
  }, [themesList, currentReponses])

  const handleReponseChange = useCallback(
    async (questionId: string, valeur: ReponseValeurV4) => {
      if (!diagnosticId || !currentMetierId) return

      const key = getReponseKey(questionId, currentMetierId)
      const existingComment = reponsesRef.current.get(key)?.commentaire

      setReponses((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(key)
        newMap.set(key, { ...existing, valeur })
        return newMap
      })

      try {
        await api.saveReponses(diagnosticId, {
          reponses: [{
            questionId,
            metierId: currentMetierId,
            valeur,
            ...(existingComment ? { commentaire: existingComment } : {}),
          }],
        })
      } catch (error) {
        console.error('Erreur sauvegarde reponse:', error)
      }
    },
    [diagnosticId, currentMetierId, getReponseKey]
  )

  const handleCommentChange = useCallback(
    async (questionId: string, commentaire: string) => {
      if (!diagnosticId || !currentMetierId) return

      const key = getReponseKey(questionId, currentMetierId)

      setReponses((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(key)
        newMap.set(key, { ...(existing || {}), commentaire })
        return newMap
      })

      const existing = reponses.get(key)
      if (existing?.valeur !== undefined) {
        try {
          await api.saveReponses(diagnosticId, {
            reponses: [{
              questionId,
              metierId: currentMetierId,
              valeur: existing.valeur,
              commentaire,
            }],
          })
        } catch (error) {
          console.error('Erreur sauvegarde commentaire:', error)
        }
      }
    },
    [diagnosticId, currentMetierId, reponses, getReponseKey]
  )

  const handleNavigateQuestion = useCallback((index: number) => {
    setCurrentQuestionIndex(index)
  }, [])

  const handleThemeChange = useCallback((themeCode: string) => {
    const theme = themesList.find((t) => t.code === themeCode)
    if (theme && theme.questions.length > 0) {
      const index = themesList.findIndex((t) => t.code === themeCode)
      setCurrentThemeIndex(index)
      setCurrentQuestionIndex(0)
    }
  }, [themesList])

  // Revenir au thème précédent (dernière question)
  const handleThemePrevious = useCallback(() => {
    const currentIdx = themesWithQuestions.findIndex(th => th.code === currentTheme?.code)
    if (currentIdx > 0) {
      const prevTheme = themesWithQuestions[currentIdx - 1]
      const globalIndex = themesList.findIndex(t => t.code === prevTheme.code)
      setCurrentThemeIndex(globalIndex)
      setCurrentQuestionIndex(prevTheme.questions.length - 1)
    }
  }, [currentTheme?.code, themesWithQuestions, themesList])

  // Vérifier si on est sur le premier thème (pour désactiver le chevron gauche)
  const isFirstTheme = useMemo(() => {
    return themesWithQuestions.length === 0 || themesWithQuestions[0]?.code === currentTheme?.code
  }, [themesWithQuestions, currentTheme])

  const handleThemeComplete = useCallback(() => {
    const nextThemeIndex = themesWithQuestions.findIndex(
      (_, idx) => idx > themesWithQuestions.findIndex(th => th.code === currentTheme?.code)
    )

    if (nextThemeIndex !== -1) {
      const nextTheme = themesWithQuestions[nextThemeIndex]
      const globalIndex = themesList.findIndex((t) => t.code === nextTheme.code)
      setCurrentThemeIndex(globalIndex)
      setCurrentQuestionIndex(0)
    }
  }, [currentTheme?.code, themesWithQuestions, themesList])

  const handleNext = useCallback(async () => {
    trackEvent('valider questionnaire q1')
    if (q2Completed && currentMetierIndex > 0) {
      const isLastMetier = currentMetierIndex >= metierIds.length - 1

      if (isLastMetier) {
        navigate(`/diagnostic/${diagnosticId}/resultats`, {
          state: {
            metiersSelectionnes,
            metierIds,
          },
        })
      } else {
        navigate(`/diagnostic/${diagnosticId}/synthese`, {
          state: {
            metiersSelectionnes,
            metierIds,
            completedMetierIndex: currentMetierIndex,
            isPartial: true,
          },
        })
      }
    } else {
      navigate(`/diagnostic/${diagnosticId}/q2`, {
        state: {
          metiersSelectionnes,
          metierIds,
          completedMetierIndex: currentMetierIndex,
        },
      })
    }
  }, [diagnosticId, navigate, metiersSelectionnes, metierIds, currentMetierIndex, q2Completed])

  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/situations`, {
      state: locationState,
    })
  }

  const totalAnswered = [...currentReponses.values()].filter(r => r.valeur !== undefined).length
  const totalQuestions = allQuestions.length
  const progressPercent = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0

  // Progression dans le thème courant
  const currentThemeProgress = themeProgress.find(t => t.code === currentTheme?.code)
  const themePercent = currentThemeProgress && currentThemeProgress.total > 0
    ? Math.round(((currentQuestionIndex + 1) / currentThemeProgress.total) * 100)
    : 0

  const getNextLabel = useMemo(() => {
    if (isSaving) return 'Sauvegarde...'

    if (q2Completed && currentMetierIndex > 0) {
      const isLastMetier = currentMetierIndex >= metierIds.length - 1
      if (isLastMetier) {
        return 'Voir les résultats'
      } else {
        const nextMetierIndex = currentMetierIndex + 1
        const nextMetier = metiersSelectionnes[nextMetierIndex]
        if (nextMetier) {
          return `Métier suivant : ${nextMetier.nom}`
        }
        return 'Synthèse intermédiaire'
      }
    }

    return 'Questionnaire Management'
  }, [isSaving, q2Completed, currentMetierIndex, metierIds.length, metiersSelectionnes])

  // Ecran de chargement
  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement du questionnaire...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={handleNext}
      backLabel="Situations"
      nextLabel={getNextLabel}
      nextDisabled={isSaving || progressPercent < 100}
      diagnosticState={diagnosticState}
    >
      {/* Layout focus — full height */}
      <div className="flex-1 flex flex-col min-h-0">
        {!showIntro && (<>
        {/* Barre unifiée : tabs thèmes + contexte + progression */}
        <div className="flex items-center gap-3 py-2 mb-2">
          {/* Tabs thèmes HALTES */}
          <div className="flex gap-1 overflow-x-auto flex-shrink-0">
            {themesList.map((theme) => {
              const progress = themeProgress.find((t) => t.code === theme.code)
              const isActive = theme.code === currentTheme?.code
              const isComplete = progress?.answered === progress?.total && (progress?.total ?? 0) > 0

              return (
                <button
                  key={theme.code}
                  onClick={() => handleThemeChange(theme.code)}
                  className={cn(
                    "flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {theme.code}
                </button>
              )
            })}
          </div>

          <div className="w-px h-5 bg-border flex-shrink-0" />

          {/* Contexte */}
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {currentTheme?.nom} · Question <span className="font-semibold text-foreground">{currentQuestionIndex + 1}</span>/{currentQuestions.length}
          </span>

          <div className="flex-1" />

          {/* Badge % global */}
          <Badge variant={progressPercent === 100 ? "default" : "secondary"} className="text-xs">
            {progressPercent}%
          </Badge>
        </div>

        {/* Barre fine progression thème courant */}
        <div className="h-1 bg-muted rounded-full mb-4">
          <div
            className="h-full bg-primary transition-all duration-300 rounded-full"
            style={{ width: `${themePercent}%` }}
          />
        </div>

        </>)}

        {/* Zone principale — flex-1 pour remplir l'espace */}
        {showIntro ? (
          <QuestionnaireIntro
            title="Questionnaire Risques"
            subtitle={<>La méthode HALTES de l'OPPBTP évalue vos pratiques de prévention sur 5 axes essentiels de la sécurité sur les chantiers : <span className="text-primary font-semibold">H</span>ygiène, <span className="text-primary font-semibold">A</span>ccès, <span className="text-primary font-semibold">L</span>ivraisons &amp; <span className="text-primary font-semibold">T</span>âches, <span className="text-primary font-semibold">É</span>nergies, <span className="text-primary font-semibold">S</span>ecours.</>}
            metierNom={currentMetier?.nom}
            questionCount={totalQuestions}
            onStart={() => setShowIntro(false)}
          />
        ) : progressPercent === 100 && !isReviewing ? (
          <Card className="overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-muted/50 border-b border-border">
              <Badge className="bg-primary hover:bg-primary text-primary-foreground">
                Questionnaire terminé
              </Badge>
            </div>

            {/* Contenu */}
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold mb-2">Bravo, vous avez terminé !</h2>

              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Vous avez répondu à toutes les questions du questionnaire Risques.
                Cliquez sur « {getNextLabel} » pour continuer.
              </p>

              <div className="flex justify-center gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{totalAnswered}</div>
                  <div className="text-sm text-muted-foreground">questions</div>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{themesWithQuestions.length}</div>
                  <div className="text-sm text-muted-foreground">thèmes complétés</div>
                </div>
              </div>

              <div className="pt-6 border-t border-border flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    const firstTheme = themesWithQuestions[0]
                    if (firstTheme) {
                      const idx = themesList.findIndex(t => t.code === firstTheme.code)
                      setCurrentThemeIndex(idx)
                      setCurrentQuestionIndex(0)
                      setIsReviewing(true)
                    }
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Vous avez un doute ? Revoir mes réponses
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          currentTheme && currentQuestions.length > 0 && (
            <QuestionWizard
              key={`${currentMetierId}-${currentTheme.code}`}
              questions={currentQuestions.map(q => ({ ...q, theme: q.themeQuestion }))}
              currentIndex={currentQuestionIndex}
              reponses={currentReponses}
              onReponseChange={handleReponseChange}
              onCommentChange={handleCommentChange}
              onNavigate={handleNavigateQuestion}
              onThemeComplete={handleThemeComplete}
              onThemePrevious={isFirstTheme ? undefined : handleThemePrevious}
              themeName={currentTheme.nom}
            />
          )
        )}
      </div>
    </DiagnosticLayout>
  )
}
